import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Joyride from 'react-joyride';
import { isMobile } from 'react-device-detect';     // this npm is used to find out if the user is on mobile browser or not

import styles from './TouchPad.module.css'
import Backdrop from '../UI/Backdrop/Backdrop';
import Blocks from '../Blocks/Blocks';
import SequenceHolder from '../SequenceHolder/SequenceHolder';
import Modals from '../UI/Modals/Modals';
import QuizContainer from "../QuizContainer/QuizContainer";
import TopRowButtons from '../UI/TopRowButtons/TopRowButtons';
import TopicHeader from '../TopicHeader/TopicHeader';
import {
    matrix, // an object with intro, body and outro as keys (also objects with name and limit of blocks that can be played)
    speed,  // speed of the flashing block intervals
    steps,   // for tooltips steps
    sortBlocks,     // to sort the selected blocks in the required fashion (1 intro, 3 body and 1 outro)
    resetFlashingBlocks,    // just as the name suggests
    clearIntervalFlashing,  // clear all flashing intervals to stop the flashing animation
    matchSequence   // to match the currently selected sequence with the already saved sequences
} from '../../helpers';
import {
    addBlock,   // adds the blocks to redux
    updateBlocks,
    setCurrentPlayingId,    // changes the currentPlayingId on redux
    setShowActiveOnly   // to hide/show the blocks' scripts
} from "../../actions/actions";

class TouchPad extends Component {
    constructor(props) {
        super(props);
        this.state = {
            modalType: null,    // to tell Modals.js, what modal to show
            tutorialNotification: false,    // the modal that appears when the app loads for the first time for any specific user
            attentionModal: false,  // modal which informs the user to select 1 intro, 3 body and 1 outro blocks
            saveSequenceModal: false,
            deleteOneSequenceModal: false,     // modal to confirm the deletion of first sequence
            deleteAllSequencesModal: false,     // modal to delete all of the sequences (triggered by clicking on X on sequence holder)
            appCloseModal: false,    // modal to close the app
            topicListOpen: false,
            isMoreInfoOpen: false,      // moreInfo displays the block's script and play/pause/volume buttons
            isSequencePlaying: false,   // when the selected sequence is playing. Triggered when play button is clicked
            playAudio: true,
            showActiveOnly: false,  // shows the selected blocks in stacked-up position
            flashing: true,
            firstWalkthroughModal: false,
            runWalkthrough: false,
            walkthroughClosed: true,
            openTopicListAutomatically: false,
            firstWalkthroughStepCompleted: false,   // for app-2 there is a step that is to be performed in between the tips. So the tips are divided into 3 parts
            secondWalkthroughStepCompleted: false,
            isQuizMode: false,
            endQuizNow: false,
            startQuizOver: false,
            difficultyLevel: 2,
            currentQuiz: null,
            totalQuizzes: 0,
            elapsedTime: 0,
            stopTimer: false,
            forceShowEndScreen: false,
            quizFinished: false,
            startTimer: false,
            disableSlider: false
        };

        this.selectBlock = this.selectBlock.bind(this);
        this.scriptListHandler = this.scriptListHandler.bind(this);
        this.scriptPlayHandler = this.scriptPlayHandler.bind(this);
        this.removeSequenceHandler = this.removeSequenceHandler.bind(this);
        this.modalsHandler = this.modalsHandler.bind(this);
        this.toggleFlashingState = this.toggleFlashingState.bind(this);
        this.clearFlashing = null;
        this.count = 0;
        this.savedSequences = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [], 10: [], 11: [], 12: [], 13: [], 14: [] };
        this.sequenceSaveLimit = 4;     // EDIT HERE ONLY if the number of sequences allowed is to be altered
        this.tempSavedSequence = null;
    }

    modalsHandler = confirmed => {
        setTimeout(() => {      // using setTimeout() so that the data from modals doesn't get removed right away
            let runWalkthrough = false;
            if (confirmed) {    // if 'Yes' button is clicked on modals
                if (this.state.modalType === 'tutorialNotification') {
                    window.localStorage.setItem('disableTutorialNotification', 'true');
                }
                if (this.state.modalType === 'firstWalkthroughModal') {
                    runWalkthrough = true;
                }
                if (this.state.modalType === 'deleteOneSequenceModal') {
                    this.deleteSequence(false);
                }
                if (this.state.modalType === 'appCloseModal') {
                    this.props.closeApp();
                }
                if (this.state.modalType === 'saveSequenceModal') {
                    this.saveSequence([...this.props.selectedBlocks]);
                    this.removeSequenceHandler();
                    this.scriptListHandler(true);
                }
            }

            this.setState({     // hiding all of the modals
                modalType: null,
                tutorialNotification: false,
                runWalkthrough,
                firstWalkthroughModal: false,
                attentionModal: false,
                saveSequenceModal: false,
                appCloseModal: false,
                deleteOneSequenceModal: false,
            });
            // this.scriptListHandler(true);   // this func clears (if true value passed) script button actions
        }, 400)
    };

    next = () => {  // this func runs only when a sequence is to be played. It will make sure that after a block (from currently selected sequence) has finished playing, the next block is to be played
        let showSaveSequenceModal = null;
        this.count++;
        if (this.props.selectedBlocks[this.count]) {
            // if we have the next block, change the currentPlayingId to the next block's id
            // Now, playing the next block (by changing the currentPlayingId) is handled in Block.js's componentDidUpdate()
            this.props.setCurrentPlayingId({ currentPlayingId: this.props.selectedBlocks[this.count].id, playOneBlockOnly: false });
            this.setState({ playAudio: false });
        }

        if (this.count === matrix['total']) {   // when all of the 5 blocks have finished playing
            if (this.savedSequences[this.props.topic.id].length > 0) {
                const matched = matchSequence(this.savedSequences[this.props.topic.id], [], this.props.selectedBlocks);     // matchSequence takes (whole sequence, one sequence, selected blocks) which matches the passed sequence (or sequences) with the selected blocks
                if (!matched) showSaveSequenceModal = 'saveSequenceModal';   // show sequence modal only when the selected sequence does not match any of the saved sequences
            } else showSaveSequenceModal = 'saveSequenceModal';  // also show sequence modal when there is no saved sequences

            this.count = 0;
            this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: true });
            this.setState({
                modalType: showSaveSequenceModal,
                saveSequenceModal: !!showSaveSequenceModal,
                playAudio: false,
                isSequencePlaying: false
            });
        }
    };

    saveSequence = sequence => {
        if (this.props.topic.id && sequence) {
            if (this.savedSequences[this.props.topic.id].length === this.sequenceSaveLimit) {    // if saved sequences' limit has already been reached
                this.setState({ modalType: 'deleteOneSequenceModal', deleteOneSequenceModal: true });   // show a modal to confirm the deletion og the first saved sequence
                this.tempSavedSequence = sequence;      // assigning the currently selected sequence to a temporary variable
            } else {
                this.savedSequences[this.props.topic.id].push(sequence);    // if a limit has not been reached, add the sequence to saved sequences
                this.setState({ modalType: null, deleteOneSequenceModal: false });  // just hide the modal then

                // for storing to localStorage
                const topicID = `${this.props.currentApp}-topic-${this.props.topic.id}-${this.savedSequences[this.props.topic.id].length}`;
                window.localStorage.setItem(topicID, JSON.stringify(sequence));
                this.props.setBlocksLocation(this.props.topic.id);
            }
        }
    };

    deleteSequence = deleteAll => {     // to delete the saved sequences (all of them or only one of them)
        if (!deleteAll) {
            this.savedSequences[this.props.topic.id].shift();   // deletes the first sequence
            this.setState({ modalType: null, deleteOneSequenceModal: false });      // hides the modal
            for (let i = 1; i <= (this.sequenceSaveLimit - 1); i++) {  // this loop is used to update the sequences stored in localStorage, accordingly
                window.localStorage.setItem(`${this.props.currentApp}-topic-${this.props.topic.id}-${i}`, window.localStorage.getItem(`topic-${this.props.topic.id}-${i + 1}`));
            }
            this.saveSequence(this.tempSavedSequence);  // save the sequence from the temporary variable
        } else {    // delete all of the sequences
            this.savedSequences[this.props.topic.id] = [];  // clearing all of the saved block sequences from current topic
            for (let j = 1; j <= this.sequenceSaveLimit; j++) {     // to remove all of the saved sequences for current topic from localStorage
                const topicID = `${this.props.currentApp}-topic-${this.props.topic.id}-${j}`;
                if (window.localStorage.getItem(topicID)) {
                    window.localStorage.removeItem(topicID);
                    this.props.resetBlocksLocation(this.props.topic.id);
                }
            }
        }
        this.tempSavedSequence = null;
    };

    toggleFlashingState = resume => { this.setState({ flashing: resume }) };

    selectBlock = block => {    // this func is run when a block is double-clicked to be selected. This func also sorts the blocks in the required manner (1 intro, 3 body and 1 outro)
        let selectedBlocks = [...this.props.selectedBlocks], blockAlreadySelected = false, blockIndex = null;
        const limit = matrix[block.type]['limit'];

        if (selectedBlocks.length > 0) {    // this will make sure the double-clicked block has not been already selected
            selectedBlocks.map((selected, index) => {
                if (selected.id === block.id) {     // this means, the selected block has already been selected
                    blockAlreadySelected = true;
                    blockIndex = index;
                }
                return null;
            })
        }

        if (blockAlreadySelected) {
            selectedBlocks.splice(blockIndex, 1);   // de-selects the block
        } else if (selectedBlocks.length <= 5) {
            if (block.type === 'body' && selectedBlocks.filter(block => block.type === 'body').length === limit) {  // if three body blocks have already been selected
                selectedBlocks = sortBlocks(selectedBlocks, limit, block, true);    // sort the blocks accordingly
            } else {
                selectedBlocks.push(block);     // if 3 body blocks have not been selected already, just add this block
                selectedBlocks = sortBlocks(selectedBlocks, limit, block, false);
            }
        }

        this.props.addBlock({ selectedBlocks, selectedId: block.id });
    };

    currentPlayingIdHandler = data => { this.props.setCurrentPlayingId(data) };

    scriptListHandler = clearAll => {   // soft reset the app state if clearAll is true
        if (clearAll) {
            this.setState({ showActiveOnly: false, modalType: null, attentionModal: false });
            this.props.setShowActiveOnly(false);
        } else {
            if (this.props.selectedBlocks.length === matrix['total']) {     // if 5 blocks are selected
                this.setState({ showActiveOnly: !this.state.showActiveOnly }, () => {
                    if (this.state.showActiveOnly) this.toggleFlashingState(false);     // if script list is open, stop the flashing of blocks
                    else if (!this.state.showActiveOnly && !this.props.currentPlayingId) this.toggleFlashingState(true);
                });

                this.props.setShowActiveOnly(!this.state.showActiveOnly);
                if (this.state.playAudio) this.setState({ playAudio: false });
            } else {
                this.setState({ modalType: 'attentionModal', attentionModal: true });
                this.toggleFlashingState(false);
            }
        }
    };

    scriptPlayHandler = () => {     // runs when play button on top is clicked
        this.count = 0;
        if (this.props.selectedBlocks && this.props.selectedBlocks.length === 5) {
            resetFlashingBlocks(this.props.selectedBlocks, this.props.updateBlocks);
            this.props.setCurrentPlayingId({ currentPlayingId: this.props.selectedBlocks[0] ? this.props.selectedBlocks[0].id : null, playOneBlockOnly: false });
            this.setState({ playAudio: false, flashing: false, isSequencePlaying: true })
        } else {
            this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: true });
            this.setState({ playAudio: true })
        }
    };

    removeSequenceHandler = () => {     // resetting the app state (triggered when clear button is clicked)
        this.props.addBlock({ selectedBlocks: [] });
        this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: true });
        this.setState({
            playAudio: false,
            flashing: true,
            isSequencePlaying: false,
        })
    };

    runWalkthrough = () => {
        this.setState({
            firstWalkthroughStepCompleted: false,
            secondWalkthroughStepCompleted: false,
            modalType: 'firstWalkthroughModal',
            firstWalkthroughModal: true,
            walkthroughClosed: false
        })
    };

    updateWalkthroughSteps = action => {  // this func is to stop the walkthrough // It will receive multiple values as the user is interacting with tooltips
        if (action === 'close') {    // value of 'close' when the user ends it by clicking the close button
            this.setState({
                openTopicListAutomatically: false,
                topicListOpen: false,
                runWalkthrough: false,  // end the walkthrough after a user has closed/finished the walkthrough tooltips
                walkthroughClosed: true,
                firstWalkthroughStepCompleted: false,
                secondWalkthroughStepCompleted: false,
            });
            this.toggleFlashingState(true);     // resume the flashing
        } else if ((this.props.currentApp === 'app-2' || this.props.currentApp === 'app-3') && action === 'reset' && !this.state.walkthroughClosed) {
            if (!this.state.firstWalkthroughStepCompleted) {
                setTimeout(() => {
                    this.setState({
                        openTopicListAutomatically: true,
                        topicListOpen: true
                    })
                }, 200);
                setTimeout(() => {
                    this.setState({ runWalkthrough: true, firstWalkthroughStepCompleted: true })
                }, 800)
            } else if (!this.state.secondWalkthroughStepCompleted) {
                this.setState({
                    openTopicListAutomatically: false,
                    topicListOpen: false,
                    runWalkthrough: false,
                    secondWalkthroughStepCompleted: true
                })
                if (this.props.currentApp === 'app-2') setTimeout(() => { this.setState({ runWalkthrough: true }) }, 600)
            } else {
                this.setState({ runWalkthrough: false })
                this.toggleFlashingState(true);     // resume the flashing
            }
        } else this.toggleFlashingState(false);  // if it has not ended yet, stop the flashing
    };

    setFlashing = blocks => {      // this func starts the flashing of blocks
        const selectedBlocks = [...blocks];
        const currentFlashingBlockIndex = selectedBlocks.findIndex(block => block.isFlashing);
        let nextSelectedBlockIndex;
        if (selectedBlocks.length > 0) {
            if (currentFlashingBlockIndex < 0) {   // if no flashing block, set the first block to flash
                selectedBlocks[0].isFlashing = true;
            } else {
                nextSelectedBlockIndex = currentFlashingBlockIndex + 1;
                selectedBlocks[currentFlashingBlockIndex].isFlashing = false;
                if (selectedBlocks[nextSelectedBlockIndex]) selectedBlocks[nextSelectedBlockIndex].isFlashing = true;
            }
            return selectedBlocks;
        }
    };

    flashEm = () => {   // this func sets the interval of their flashing
        this.clearFlashing = setInterval(() => {
            const selectedBlocks = this.setFlashing(this.props.selectedBlocks);
            this.props.updateBlocks(selectedBlocks);
        }, speed);      // speed = 300 here
        window.clearFlashing = this.clearFlashing;
    };

    stopFlashing = () => {  // this is used to pause the flashing
        this.clearFlashing = clearIntervalFlashing(this.clearFlashing);
        clearInterval(this.clearFlashing);
        this.clearFlashing = null;
    };

    getWalkthroughDetails = () => {
        let joyrideSteps = steps[0];    // steps received from 'helpers.js' is an array of 3 arrays (1 array for each app) of objects (tooltip steps)
        let joyrideLocale = { back: 'Previous', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' };
        if (this.props.currentApp === 'app-2') {
            joyrideSteps = steps[1];
            joyrideLocale = { back: 'Previous', close: 'Close', last: 'Next', next: 'Next', skip: 'Skip' };
            if (this.state.firstWalkthroughStepCompleted) joyrideSteps = steps[2];
            if (this.state.secondWalkthroughStepCompleted) {
                joyrideSteps = steps[3];
                joyrideLocale = { back: 'Previous', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' };
            }
        }
        if (this.props.currentApp === 'app-3') {
            joyrideSteps = steps[4];
            joyrideLocale = { back: 'Previous', close: 'Close', last: 'Next', next: 'Next', skip: 'Skip' };
            if (this.state.firstWalkthroughStepCompleted) {
                joyrideSteps = steps[5];
                joyrideLocale = { back: 'Previous', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' };
            }
        }
        return [joyrideSteps, joyrideLocale]
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.selectedBlocks !== prevProps.selectedBlocks) {
            if (this.props.selectedBlocks && this.props.selectedBlocks.length > 0 && !this.props.currentPlayingId) {     // if some blocks are selected and none is playing
                if (!this.clearFlashing) this.flashEm();
            } else this.stopFlashing();
        } else this.stopFlashing();

        if (this.state.flashing !== prevState.flashing) {
            if (this.props.selectedBlocks && this.props.selectedBlocks.length > 0) {
                if (this.state.flashing) this.flashEm();
                else this.stopFlashing();
            }
        }

        if (this.props.currentPlayingId !== prevProps.currentPlayingId) {
            // to resume the flashing only when there is no block playing,
            // flashing state is NOT already set to true, moreInfo is not open,
            // script list is closed and sequence is NOT playing
            if (
                !this.props.currentPlayingId &&
                !this.state.flashing &&
                !this.state.isMoreInfoOpen &&
                !this.state.showActiveOnly &&
                !this.state.isSequencePlaying
            ) this.toggleFlashingState(true);
        }

        if (this.props.toggleHeader !== prevProps.toggleHeader) {   // when the topic list is opened/closed
            this.setState({
                topicListOpen: this.props.toggleHeader,
                runWalkthrough: (this.props.toggleHeader && this.state.runWalkthrough) ? false : this.state.runWalkthrough
            }, () => { this.toggleFlashingState(!this.state.topicListOpen) })
        }

        if (prevState.modalType !== this.state.modalType) {
            if (!!this.state.modalType) this.toggleFlashingState(false);
            else this.toggleFlashingState(true);
        }

        if (this.props.currentApp === 'app-3') {
            if (prevProps.selectedTopicId !== this.props.selectedTopicId) {
                if (this.state.isQuizMode) this.setState({ endQuizNow: true })
            }
        }

        if (prevState.timeOver !== this.state.timeOver) {
            if (this.state.timeOver) {
                this.setState({ forceShowEndScreen: true, timeOver: false })
            }
        }
    }

    componentWillUnmount() {
        this.clearFlashing && clearInterval(this.clearFlashing);
        this.clearFlashing = null;
        this.clearFlash && clearInterval(this.clearFlash);
        this.clearFlash = null;
        this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: false });
    }

    componentDidMount() {
        let savedSequences = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [], 10: [], 11: [], 12: [], 13: [], 14: [] };
        // here: keys are topic ids and values are arrays of blocks. The max number of blocks an array can have is limited by this.sequenceSaveLimit
        for (let i = 1; i <= 14; i++) {
            for (let j = 1; j <= this.sequenceSaveLimit; j++) {
                let topicID = `${this.props.currentApp}-topic-${i}-${j}`;
                if (window.localStorage.getItem(topicID)) savedSequences[i].push(JSON.parse(window.localStorage.getItem(topicID)));
                // the sequences are saved to localStorage with respective topics, defined by keys.
                // The keys used are in the format: topic-X-Y -> where X is topic number (id) [ranging from 1 to 14] and Y is sequence number [ranging from 1 to this.sequenceSaveLimit]
            }
        }
        this.savedSequences = savedSequences;

        // to show the first-time modal after some set time
        if (!window.localStorage.getItem('disableTutorialNotification')) {
            setTimeout(() => {
                this.setState({
                    modalType: 'tutorialNotification',
                    tutorialNotification: true,
                })
            }, 1500)
        }
    }

    render() {
        // we remove top row buttons
        const appRowStyle = this.props.currentApp === 'app-3' ? { margin: '30px auto 0', maxWidth: '640px' } : {};

        // getting all the walkthrough tips information
        const [joyrideSteps, joyrideLocale] = this.getWalkthroughDetails();

        // this variable is to disable the sequence holder buttons on certain conditions
        const buttonsDisabled = (
            !!this.props.currentPlayingId ||
            !!this.state.modalType ||
            this.state.isMoreInfoOpen ||
            this.state.isSequencePlaying ||
            this.state.showActiveOnly
        );

        return (
            <div className={`${styles.AppContainer} ${isMobile ? 'disable-text-select' : ''}`}>
                <Backdrop show={this.props.toggleHeader} fadeBackdrop={true} close={() => this.setState({ topicListOpen: false })} />
                <Modals
                    type={this.state.modalType}
                    modalsHandler={confirmed => this.modalsHandler(confirmed)}  // 'confirmed' will be true if 'Yes' button is clicked, else it will be null
                />

                {/* for walkthrough tooltips */}
                <Joyride
                    steps={joyrideSteps}
                    continuous={true}
                    locale={joyrideLocale}
                    callback={status => this.updateWalkthroughSteps(status.action)}
                    run={this.state.runWalkthrough}
                    showProgress={false}
                    disableOverlayClose={true}
                    styles={{ options: { zIndex: 1049 } }}
                />

                <SequenceHolder
                    savedSequences={this.savedSequences}
                    sequenceSaveLimit={this.sequenceSaveLimit}
                    selectedTopicId={this.props.topic.id}
                    savedSequenceLimitReached={this.state.savedSequenceLimitReached}
                    deleteAllSequencesModal={status => this.setState({ deleteAllSequencesModal: status })}
                    deleteOneSequence={() => this.deleteSequence(false)}
                    deleteAllSequence={() => this.deleteSequence(true)}
                    sequenceButtonsDisabled={buttonsDisabled || this.state.topicListOpen}
                />

                {/* top header with topic name and audio buttons */}
                <TopicHeader
                    scriptListHandler={this.scriptListHandler}
                    scriptPlayHandler={this.scriptPlayHandler}
                    removeSequenceHandler={this.removeSequenceHandler}
                    showActiveOnly={this.state.showActiveOnly}
                    topicListOpen={this.state.topicListOpen}
                    openTopicListAutomatically={this.state.openTopicListAutomatically}
                    buttonsDisabled={buttonsDisabled}
                    endQuizModeButtonClicked={() => { this.setState({ endQuizNow: true }) }}
                    showQuizIntro={() => this.setState({ showQuizIntro: true })}
                    helpButtonClicked={() => this.runWalkthrough()}
                    closeButtonClicked={() => this.setState({ modalType: 'appCloseModal', appCloseModal: true })}
                    updateQuizLevel={this.state.updateQuizLevel ? this.state.difficultyLevel : null}
                    updateDifficultyState={value => this.setState({ difficultyLevel: value })}
                    quizMode={this.state.startTimer}
                    quizStats={[this.state.currentQuiz, this.state.totalQuizzes]}
                    startTimer={this.state.startTimer}
                    elapsedTime={val => this.setState({ elapsedTime: val }) }
                    timeOver={() => this.setState({ timeOver: true })}
                    startQuizOver={this.state.startQuizOver}
                    quizStarted={() => this.setState({ startQuizOver: false })}
                    quizFinished={this.state.quizFinished}
                    resetQuizFinishedState={() => this.setState({ quizFinished: false, startTimer: false })}
                    disableSlider={this.state.disableSlider}
                />

                {/* buttons with INTRO -> 1 -> 2 -> 3 -> OUTRO */}
                <TopRowButtons selectedBlocks={this.props.selectedBlocks} />

                {/* container for all the blocks. IF we are in Quiz Mode, we don't show blocks */}
                <div className='app-row-main' style={appRowStyle}>
                    {
                        this.props.currentApp === 'app-3' && this.state.showQuizIntro ?
                        <QuizContainer
                            difficultyLevel={this.state.difficultyLevel}
                            allBlocks={this.props.allBlocks}
                            quizMode={true}
                            startQuiz={() => this.setState({ isQuizMode: true, endQuizNow: false }) }
                            startTimer={ () => this.setState({ startTimer: true }) }
                            updateQuizStats ={(currentQuiz, totalQuizzes) => this.setState({currentQuiz, totalQuizzes})}
                            quizModeFinished={() => this.setState({ isQuizMode: false, endQuizNow: false, showQuizIntro: false, startTimer: false })}
                            endQuizNow={this.state.endQuizNow}
                            forceShowEndScreen={this.state.forceShowEndScreen}
                            donewithEndScreen={() => this.setState({ forceShowEndScreen: false })}
                            elapsedTime={this.state.elapsedTime}
                            startQuizOver={() => this.setState({ startQuizOver: true })}
                            quizFinished={() => this.setState({ quizFinished: true })}
                            changeDifficultyLevel={difficultyLevel => this.setState({ difficultyLevel, updateQuizLevel: true })}
                            startNewQuiz={this.props.startNewQuiz}
                        />
                        :
                        <Blocks
                            topic={this.props.topic}
                            selectedBlocks={this.props.selectedBlocks}
                            scriptPlayHandler={this.scriptPlayHandler}
                            color={this.props.colors}
                            blockSelector={this.selectBlock}
                            selectedId={this.props.selectedId}
                            showActiveOnly={this.props.showActiveOnly}
                            resumeFlashing={() => this.toggleFlashingState(true)}
                            stopFlashing={() => this.toggleFlashingState(false)}
                            next={this.next}
                            setCurrentPlayingId={this.currentPlayingIdHandler}
                            flashingStatus={this.state.flashing}
                            setMoreInfoStatus={status => this.setState({isMoreInfoOpen: status})}
                            moreInfoStatus={this.state.isMoreInfoOpen}
                            scriptListStatus={this.state.showActiveOnly}
                            isSequencePlaying={this.state.isSequencePlaying}
                            difficultyLevel={this.state.difficultyLevel}
                            disableSlider={status => this.setState({ disableSlider: status }) }
                        />
                    }
                </div>
            </div>
        );
    }
}

TouchPad.propTypes = {
    selectedBlocks: PropTypes.array,
    selectedId: PropTypes.number
};

const mapStateToProps = state => ({
    currentApp: state.appReducer.currentApp,
    selectedBlocks: state.touchPadReducer.selectedBlocks || [],
    totalSelectedBlocks: state.touchPadReducer.totalSelectedBlocks || 0,
    currentPlayingId: state.touchPadReducer.currentPlayingId,
    showActiveOnly: state.touchPadReducer.showActiveOnly,
    selectedTopicId: state.topicListReducer.selectedTopicId,
    colors: state.topicListReducer.topicColors,
    topic: state.topicListReducer.selectedTopic,
    toggleHeader: state.topicHeaderReducer.toggleHeader || false,
    topicList: state.topicListReducer.topics || [],
});

const mapDispatchToProps = dispatch => ({
    addBlock: params => dispatch(addBlock(params)),
    updateBlocks: params => dispatch(updateBlocks(params)),
    setCurrentPlayingId: params => dispatch(setCurrentPlayingId(params)),
    setShowActiveOnly: params => dispatch(setShowActiveOnly(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TouchPad);